import { decodeCreated, decodePurposeOne } from '#cmp/tcstring.js';
import { getCookie } from '#cmp/cookie.js';
export const CONSENT_COOKIE_NAME = 'euconsent-v2';
export const CONSENT_PURPOSE_ONE_COOKIE_NAME = 'szncmpone';
export const CONSENT_GOOGLE_COOKIE_NAME = 'FCCDCF';
export const CONSENT_COOKIE_NAMES = [
    CONSENT_COOKIE_NAME,
    'eupubconsent-v2',
    'ibb_euconsent',
    'gdpr_consent',
    'sas_cmp_cookie_consentData',
    'csent',
    CONSENT_GOOGLE_COOKIE_NAME,
];
/**
 * Extract consent string from cookie (special cases -> tc string wrapped inside non primitive).
 */
const extractConsentString = (cookieName, cookieValue) => {
    try {
        // GOOGLE consent
        if (cookieName === CONSENT_GOOGLE_COOKIE_NAME && cookieValue) {
            return JSON.parse(decodeURIComponent(cookieValue))[3][0];
        }
    }
    catch {
        return null;
    }
    // trust nobody
    return cookieValue && typeof cookieValue === 'string' ? cookieValue : null;
};
export const PurposeId = {
    /**
     * Purpose one is not supposed to be saved to purposeLegitimateInterests because we have purposeOneTreatment true,
     * but it should be saved to purposeConsents, because RTB needs it.
     */
    COOKIE: 1,
    // implicit consent
    BASIC_ADS: 2,
    MEASURE_ADS: 7,
    MEASURE_PERFORMANCE: 8,
    MARKET_RESEARCH: 9,
    IMPROVE: 10,
    // user choice
    ADS_PROFILE: 3,
    ADS: 4,
    CONTENT_PROFILE: 5,
    CONTENT: 6,
};
/** Returns true if the consent is expired or
 * otherwise invalid (no created date).
 */
const isExpired = (cookieValue) => {
    const created = decodeCreated(cookieValue);
    if (!created) {
        return true; // no created date -> expired
    }
    const thirteenMonthsAgo = new Date();
    thirteenMonthsAgo.setMonth(thirteenMonthsAgo.getMonth() - 13);
    // TCF operates on day granularity
    thirteenMonthsAgo.setHours(0, 0, 0, 0);
    return created < thirteenMonthsAgo;
};
export const hasPurposeOne = (consentString) => {
    if (typeof consentString !== 'string') {
        return false;
    }
    return decodePurposeOne(consentString);
};
const getResult = (euconsentString, name) => {
    return {
        version: 'v2',
        consentString: euconsentString,
        name,
        purposeOne: hasPurposeOne(euconsentString),
    };
};
// wait for TCF API v2 for TCData
let receivedTCFData = '';
let listeningTCFApi = false;
const receiverCallbackList = [];
/** Resets global state of this module.
 * intended to be only used for testing.
 */
export const __resetTCFGlobals = () => {
    receivedTCFData = '';
    receiverCallbackList.length = 0;
    // do not clear tcfapi, that would cause the tests
    // to attach the listener again
};
const tcCallback = (tcData, success) => {
    if (success && ['tcloaded', 'useractioncomplete'].includes(tcData.eventStatus)) {
        // TCF API should not have expired consent string,
        // we are just being extra careful here.
        if (isExpired(tcData.tcString)) {
            return;
        }
        // do something with tcData.tcString
        receivedTCFData = tcData.tcString;
        const result = getResult(receivedTCFData);
        for (const receiverCallback of receiverCallbackList) {
            receiverCallback(result);
        }
        receiverCallbackList.length = 0;
    }
};
const hasTCFApi = () => typeof window.__tcfapi === 'function';
// listen to TCF api (just once)
const listenToTCFApi = () => {
    if (hasTCFApi() && !listeningTCFApi) {
        listeningTCFApi = true;
        window.__tcfapi('addEventListener', 2, tcCallback);
    }
};
listenToTCFApi();
/**
 * Get consent TCF cookie from predefined list.
 * @param receiverCallback if set you can wait until consent is set using TCF API v2
 * or you get no result it mean there is no TCF API
 * @returns Object ConsentResult from existing cookie or null if not found.
 */
export const getConsentFromCookieList = (receiverCallback) => {
    if (!document) {
        return null;
    }
    // in some cases TCF api is available after some time -> attach listener (if not already attached)
    listenToTCFApi();
    for (const cookie of CONSENT_COOKIE_NAMES) {
        const cookieValue = extractConsentString(cookie, getCookie(cookie));
        if (cookieValue && !isExpired(cookieValue)) {
            return getResult(cookieValue, cookie);
        }
    }
    // TCF API should not have expired consent string,
    // we are just being extra careful here.
    if (receivedTCFData && !isExpired(receivedTCFData)) {
        return getResult(receivedTCFData);
    }
    else if (typeof receiverCallback === 'function') {
        if (hasTCFApi()) {
            receiverCallbackList.push(receiverCallback);
        }
        else {
            receiverCallback();
        }
    }
    return null;
};
export const hasPurposeOneFromCookieList = () => {
    const consent = getConsentFromCookieList();
    return consent ? hasPurposeOne(consent.consentString) : false;
};
